import {
	Check,
	DraftingCompass,
	HardHat,
	House,
	KeyRound,
	type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	getPostAuthLanding,
	readRedirectTo,
} from "@/features/auth/utils/post-auth-redirect";
import { StudioButton } from "@/features/studio/theme";

export type UserType = "homeowner" | "designer" | "contractor" | "realtor";

interface UserTypeOption {
	type: UserType;
	titleKey: string;
	descKey: string;
	icon: LucideIcon;
}

const userTypeOptions: UserTypeOption[] = [
	{
		type: "homeowner",
		titleKey: "userTypes.homeowner",
		descKey: "userTypes.homeownerDesc",
		icon: House,
	},
	{
		type: "designer",
		titleKey: "userTypes.designer",
		descKey: "userTypes.designerDesc",
		icon: DraftingCompass,
	},
	{
		type: "contractor",
		titleKey: "userTypes.contractor",
		descKey: "userTypes.contractorDesc",
		icon: HardHat,
	},
	{
		type: "realtor",
		titleKey: "userTypes.realtor",
		descKey: "userTypes.realtorDesc",
		icon: KeyRound,
	},
];

export function SelectUserTypePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		setUserType,
		isLoading,
		user,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		logout,
	} = useAuthStore();
	const [selected, setSelected] = useState<UserType | null>(null);
	// Set once Back has been pressed, so a second press cannot repeat it.
	const [isLeaving, setIsLeaving] = useState(false);

	const allowSelection = location.state?.allowSelection === true;
	// Forwarded on every hop that continues the flow, so the page the visitor
	// was originally heading for survives choosing a role and filling a form.
	// Omitted entirely when there is nothing to forward, so a visitor who
	// arrived here directly navigates exactly as they did before.
	const redirectTo = readRedirectTo(location.state);

	// Redirect if already has user type
	useEffect(() => {
		if (allowSelection) return;

		if (user && !needsOnboarding()) {
			if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`, {
					replace: true,
					...(redirectTo ? { state: { redirectTo } } : {}),
				});
			} else {
				navigate(redirectTo ?? getPostAuthLanding(getUserType()), {
					replace: true,
				});
			}
		}
	}, [
		user,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		navigate,
		allowSelection,
		redirectTo,
	]);

	const handleBack = async () => {
		if (isLeaving) return;
		setIsLeaving(true);

		// Arrived from a role's onboarding page with a finished session: step back
		// to it and stay signed in. The first entry of a session has the key
		// "default"; with no earlier entry (a fresh tab) go to the landing page.
		if (allowSelection) {
			if (location.key === "default") {
				navigate(getPostAuthLanding(getUserType()), { replace: true });
			} else {
				navigate(-1);
			}
			return;
		}

		// No role yet: leave the sign-up flow by signing out.
		try {
			await logout();
		} catch {
			// The store clears its state in `finally`, so the session is gone either
			// way. There is nothing useful to report; still leave.
		}
		navigate("/login", { replace: true });
	};

	const handleContinue = async () => {
		if (!selected) {
			toast.error(t("userTypes.pleaseSelect"));
			return;
		}

		try {
			await setUserType(selected);
			toast.success(t("userTypes.profileTypeSet"));

			// Navigate to appropriate onboarding. Each role has its own
			// flow because the data we need to collect differs significantly.
			// Called with no options when there is nothing to forward, so the
			// ordinary sign-up path navigates exactly as it did before.
			if (redirectTo) {
				navigate(`/onboarding/${selected}`, { state: { redirectTo } });
			} else {
				navigate(`/onboarding/${selected}`);
			}
		} catch {
			toast.error(t("userTypes.failedToSetType"));
		}
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-dashboard-warm-350 px-4 py-10">
			<div className="w-full max-w-2xl">
				<button
					type="button"
					onClick={handleBack}
					className="mb-6 flex items-center gap-2 font-body text-sm text-dashboard-muted transition-colors duration-200 hover:text-dashboard-text"
				>
					<svg
						className="h-4 w-4"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M10 19l-7-7m0 0l7-7m-7 7h18"
						/>
					</svg>
					{t("common.back")}
				</button>

				<div className="mb-8 text-center">
					<h1
						id="select-role-title"
						className="mb-2 font-display text-[28px] font-semibold leading-tight text-dashboard-text sm:text-[32px]"
					>
						{t("userTypes.selectTitle")}
					</h1>
					<p className="font-body text-sm text-dashboard-muted">
						{t("userTypes.selectSubtitle")}
					</p>
				</div>

				{/* Native radios, visually hidden: arrow keys, focus and the
				    checked state come from the browser, not from this file. */}
				<div
					role="radiogroup"
					aria-labelledby="select-role-title"
					className="grid gap-3 sm:grid-cols-2 sm:gap-4"
				>
					{userTypeOptions.map((option) => {
						const isSelected = selected === option.type;
						const Icon = option.icon;
						return (
							<label
								key={option.type}
								className={`relative flex cursor-pointer gap-4 rounded-[13px] border p-5 transition-all duration-200 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-dashboard-accent sm:flex-col sm:gap-5 sm:p-6 ${
									isSelected
										? "border-dashboard-accent bg-dashboard-warm-450 shadow-[0_6px_20px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]"
										: "border-dashboard-border bg-dashboard-surface shadow-[0_1px_2px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_5%,transparent)] hover:border-dashboard-accent/40"
								}`}
							>
								<input
									type="radio"
									name="user-type"
									value={option.type}
									checked={isSelected}
									onChange={() => setSelected(option.type)}
									className="sr-only"
								/>
								<span
									aria-hidden="true"
									className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] transition-colors duration-200 ${
										isSelected
											? "bg-dashboard-accent text-dashboard-on-fill"
											: "border border-dashboard-border bg-dashboard-warm-450 text-dashboard-accent-dark"
									}`}
								>
									<Icon className="h-6 w-6" strokeWidth={1.6} />
								</span>
								<span className="min-w-0">
									<span className="block font-display text-lg font-semibold text-dashboard-text">
										{t(option.titleKey)}
									</span>
									<span className="mt-1 block font-body text-sm leading-relaxed text-dashboard-muted">
										{t(option.descKey)}
									</span>
								</span>
								{isSelected && (
									<span
										aria-hidden="true"
										className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-dashboard-accent text-dashboard-on-fill"
									>
										<Check className="h-3.5 w-3.5" strokeWidth={3} />
									</span>
								)}
							</label>
						);
					})}
				</div>

				<StudioButton
					type="button"
					variant="primary"
					disabled={!selected || isLoading}
					onClick={handleContinue}
					style={{ width: "100%", marginTop: 32 }}
				>
					{isLoading ? t("userTypes.settingUp") : t("common.continue")}
				</StudioButton>

				<p className="mt-4 text-center font-body text-sm text-dashboard-muted">
					{t("userTypes.changeLater")}
				</p>
			</div>
		</div>
	);
}
